import type { HighlightTerm } from "../types";

/** A run of a result's text, and whether it is part of what was searched for. Data for a view to style: never HTML. */
export interface TextSegment {
	text: string;
	match: boolean;
}

// The nearest JS comes to Python's `str.casefold`, which the API matches with: going through upper case is what folds
// ß to ss and ς to σ, which `toLowerCase` alone leaves.
const fold = (text: string) => text.toLowerCase().toUpperCase().toLowerCase();

// A word by the API's `word_text`: every other character, the underscore included, is a boundary.
const WORD_CHARACTER = "[\\p{L}\\p{N}]";
const NOT_A_WORD_CHARACTER = "[^\\p{L}\\p{N}]";

/** A phrase's whole words, together and in order. Null if it holds no word. */
function phrasePattern(folded: string): RegExp | null {
	const words = folded.split(new RegExp(`${NOT_A_WORD_CHARACTER}+`, "u")).filter(Boolean);
	if (words.length === 0) return null;
	const run = words.join(`${NOT_A_WORD_CHARACTER}+`);
	return new RegExp(`(?<!${WORD_CHARACTER})${run}(?!${WORD_CHARACTER})`, "gu");
}

/** Splits `text` around what `terms` match, by the API's rule: a term is a casefolded substring, a phrase its whole
 *  words. A term marks wherever it lands in `text`, whichever field it was filtered to. */
export function highlightMatches(text: string, terms: readonly HighlightTerm[]): TextSegment[] {
	if (!text) return [];

	// Folding can change a character's length, so each position of the folded text remembers its character.
	const characters = Array.from(text);
	let folded = "";
	const characterAt: number[] = [];
	characters.forEach((character, index) => {
		const foldedCharacter = fold(character);
		folded += foldedCharacter;
		for (let unit = 0; unit < foldedCharacter.length; unit++) characterAt.push(index);
	});

	const isMatch = new Array<boolean>(characters.length).fill(false);
	const mark = (at: number, length: number) => {
		for (let unit = at; unit < at + length; unit++) isMatch[characterAt[unit]] = true;
	};
	for (const { text: term, phrase } of terms) {
		const token = fold(term);
		if (phrase) {
			const pattern = phrasePattern(token);
			if (pattern) for (const run of folded.matchAll(pattern)) mark(run.index, run[0].length);
		} else {
			for (let at = folded.indexOf(token); at !== -1; at = folded.indexOf(token, at + 1)) mark(at, token.length);
		}
	}

	const segments: TextSegment[] = [];
	characters.forEach((character, index) => {
		const last = segments.at(-1);
		if (last?.match === isMatch[index]) last.text += character;
		else segments.push({ text: character, match: isMatch[index] });
	});
	return segments;
}
